import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Pencil, X, Eye } from 'lucide-react';
import { CopyButton, ColoredAmount, LoadingButton, ConfirmDialog, isLargeAmount } from '../components/QuickWins';
import ReviewEntryDialog from '../components/ReviewEntryDialog';
import CashConversionTable from '../components/CashConversionTable';

const AdminApprovals = () => {
  const [approvals, setApprovals] = useState({ ledger_entries: [], expenses: [], cash_conversions_inr_to_usdt: [], cash_conversions_usdt_to_inr: [], total_pending: 0 });
  const [changesQueue, setChangesQueue] = useState({ ledger_entries: [], expenses: [], cash_conversions_inr_to_usdt: [], cash_conversions_usdt_to_inr: [], total_pending: 0 });
  const [currentUser, setCurrentUser] = useState(null);
  const [currentPoolBalance, setCurrentPoolBalance] = useState(0);
  const [lastApprovedInrToUsdtRate, setLastApprovedInrToUsdtRate] = useState(null);
  const [lastApprovedUsdtToInrRate, setLastApprovedUsdtToInrRate] = useState(null);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'changes_requested'
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [exchanges, setExchanges] = useState([]);

  // Edit Modal States
  const [editLedgerModal, setEditLedgerModal] = useState({ open: false, entry: null });
  const [editExpenseModal, setEditExpenseModal] = useState({ open: false, expense: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', id: '', entry: null });

  // Review Dialog State
  const [reviewDialog, setReviewDialog] = useState({ open: false, entry: null, kind: null });

  useEffect(() => {
    fetchPendingApprovals();
    fetchClients();
    fetchCategories();
    fetchUsers();
    fetchExchanges();
  }, []);

  const fetchPendingApprovals = async () => {
    try {
      const [pRes, cRes, meRes, poolRes, convHistRes, u2iHistRes] = await Promise.all([
        axiosInstance.get('/admin/pending-approvals'),
        axiosInstance.get('/admin/pending-approvals?status=changes_requested'),
        axiosInstance.get('/auth/me').catch(() => ({ data: null })),
        axiosInstance.get('/cash-pool/balance').catch(() => ({ data: null })),
        axiosInstance.get('/cash-pool/conversions').catch(() => ({ data: [] })),
        axiosInstance.get('/cash-pool/usdt-to-inr').catch(() => ({ data: [] })),
      ]);
      setApprovals(pRes.data);
      setChangesQueue(cRes.data);
      setCurrentUser(meRes.data);
      setCurrentPoolBalance(poolRes.data?.balance ?? 0);
      const lastInrToUsdt = (convHistRes.data || []).find((c) => (c.approval_status || 'approved') === 'approved');
      const lastUsdtToInr = (u2iHistRes.data || []).find((c) => (c.approval_status || 'approved') === 'approved');
      setLastApprovedInrToUsdtRate(lastInrToUsdt?.conversion_rate ?? null);
      setLastApprovedUsdtToInrRate(lastUsdtToInr?.conversion_rate ?? null);
    } catch (error) {
      toast.error('Failed to fetch pending approvals');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get('/clients');
      // Handle new clients API response structure
      const clientsData = response.data.clients || response.data;
      setClients(clientsData);
    } catch (error) {
      console.error('Failed to fetch clients');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/expense-categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data || []);
    } catch (error) {
      // Non-admin users won't have access — silently degrade to id-only display.
    }
  };

  const fetchExchanges = async () => {
    try {
      const response = await axiosInstance.get('/exchanges');
      setExchanges(response.data || []);
    } catch (error) {
      // Optional context — silent fallback.
    }
  };

  const openReview = (entry, kind) => {
    setReviewDialog({ open: true, entry, kind });
  };
  const closeReview = () => setReviewDialog({ open: false, entry: null, kind: null });

  const approveLedgerEntry = async (entryId, entry = null) => {
    // Check for large amounts
    if (entry && entry.type === 'deposit' && isLargeAmount(entry.amount, entry.currency)) {
      setConfirmDialog({ 
        open: true, 
        type: 'approveLedger', 
        id: entryId, 
        entry,
        title: 'Approve Large Deposit',
        message: `You are about to approve a deposit of ${entry.currency} ${entry.amount.toLocaleString('en-IN')} (above ₹50 Lakhs threshold). Please confirm.`
      });
      return;
    }
    
    await doApproveLedger(entryId);
  };
  
  const doApproveLedger = async (entryId) => {
    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/ledger/${entryId}/approve`);
      toast.success('Ledger entry approved successfully');
      fetchPendingApprovals();
      setConfirmDialog({ open: false, type: '', id: '', entry: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const rejectLedgerEntry = async (entryId, clientCode = '') => {
    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/ledger/${entryId}/reject`);
      toast.success(`Ledger entry for ${clientCode || 'client'} rejected`);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  const approveExpense = async (expenseId, category = '') => {
    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/expenses/${expenseId}/approve`);
      toast.success(`Expense "${category}" approved`);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  const rejectExpense = async (expenseId, category = '') => {
    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/expenses/${expenseId}/reject`);
      toast.success(`Expense "${category}" rejected`);
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit Ledger Entry
  const openEditLedger = (entry) => {
    setEditLedgerModal({ 
      open: true, 
      entry: { ...entry }
    });
  };

  const handleLedgerEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const entry = editLedgerModal.entry;
      await axiosInstance.put(`/ledger/${entry.id}`, {
        type: entry.type,
        currency: entry.currency,
        amount: parseFloat(entry.amount),
        deposit_method: entry.deposit_method,
        conversion_rate: parseFloat(entry.conversion_rate) || 85,
        exclude_from_current_cycle: entry.exclude_from_current_cycle,
        withdrawal_source: entry.withdrawal_source,
        date: entry.date,
        notes: entry.notes
      });
      toast.success(`Ledger entry for ${entry.client_code} updated`);
      setEditLedgerModal({ open: false, entry: null });
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update entry');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit Expense
  const openEditExpense = (expense) => {
    setEditExpenseModal({ 
      open: true, 
      expense: { ...expense }
    });
  };

  const handleExpenseEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const expense = editExpenseModal.expense;
      await axiosInstance.put(`/expenses/${expense.id}`, {
        date: expense.date,
        category: expense.category,
        sub_category: expense.sub_category || null,
        amount: parseFloat(expense.amount),
        currency: expense.currency,
        conversion_rate: parseFloat(expense.conversion_rate) || 1,
        payment_source: expense.payment_source,
        notes: expense.notes
      });
      toast.success(`Expense "${expense.category}" updated`);
      setEditExpenseModal({ open: false, expense: null });
      fetchPendingApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="approvals-page">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Governance</p>
            <h1 className="cb-h1">Admin Approvals</h1>
            <p className="cb-section-sub cb-num" style={{ marginTop: 'var(--cb-space-2)' }}>
              {approvals.total_pending} pending approval{approvals.total_pending !== 1 ? 's' : ''}
              {changesQueue.total_pending > 0 && (
                <> · {changesQueue.total_pending} awaiting resubmit</>
              )}
            </p>
          </div>
          <div className="flex items-center" style={{ gap: 'var(--cb-space-2)' }}>
            <Clock className="w-5 h-5" style={{ color: 'var(--cb-ink-soft)' }} />
            <span className="cb-pill cb-num">{approvals.total_pending} Pending</span>
          </div>
        </div>
        <hr className="cb-divider" />
      </div>

      {/* Queue Tabs */}
      <div className="cb-segment" style={{ marginBottom: 'var(--cb-space-5)' }} data-testid="approvals-queue-tabs">
        <button
          type="button"
          className={`cb-btn-secondary ${activeTab === 'pending' ? 'cb-btn-secondary--active' : ''}`}
          style={{ border: 'none' }}
          onClick={() => setActiveTab('pending')}
          data-testid="tab-pending"
        >
          Pending ({approvals.total_pending})
        </button>
        <button
          type="button"
          className={`cb-btn-secondary ${activeTab === 'changes_requested' ? 'cb-btn-secondary--active' : ''}`}
          style={{ border: 'none' }}
          onClick={() => setActiveTab('changes_requested')}
          data-testid="tab-changes-requested"
        >
          Changes Requested ({changesQueue.total_pending})
        </button>
      </div>

      {(() => {
        const view = activeTab === 'changes_requested' ? changesQueue : approvals;
        const ledgerLabel = activeTab === 'changes_requested' ? 'Ledger Entries Awaiting Resubmit' : 'Pending Ledger Entries';
        const expenseLabel = activeTab === 'changes_requested' ? 'Expenses Awaiting Resubmit' : 'Pending Expenses';
        return (
          <>

      {/* Ledger Entries */}
      {view.ledger_entries.length > 0 && (
        <div className="mb-8">
          <h2 className="cb-section-title mb-4" data-testid="ledger-section-title">{ledgerLabel} ({view.ledger_entries.length})</h2>
          <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table data-testid="pending-ledger-table" className="cb-table" style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Client</th>
                  <th>Type</th>
                  <th>Currency</th>
                  <th className="cb-num">Amount</th>
                  <th className="cb-num">USDT Equiv.</th>
                  <th>Method/Source</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {view.ledger_entries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="cb-num cb-muted" style={{ fontSize: '0.8125rem' }}>{new Date(entry.date).toLocaleDateString()}</td>
                    <td style={{ fontWeight: 500 }}>
                      {entry.client_code}
                      <CopyButton text={entry.client_code} label="Client Code" />
                    </td>
                    <td>
                      <span className="cb-pill">
                        {entry.type}
                      </span>
                      {entry.approval_status === 'changes_requested' && (
                        <span
                          className="cb-pill"
                          style={{ marginLeft: 'var(--cb-space-1)', fontSize: '0.6875rem', borderColor: 'var(--cb-warning, #b47a00)', color: 'var(--cb-warning, #b47a00)' }}
                          title={(entry.review_notes||[]).slice(-1).map(n => `${n.by}: ${n.note}`).join('') || 'Sent back for changes'}
                          data-testid={`changes-requested-badge-${entry.id}`}
                        >
                          changes requested
                        </span>
                      )}
                    </td>
                    <td>{entry.currency}</td>
                    <td className="cb-num">
                      {(entry.currency === 'INR' ? '₹' : '')}{entry.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="cb-num cb-muted">{entry.usdt_equivalent ? entry.usdt_equivalent.toLocaleString() : '-'}</td>
                    <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>
                      {entry.type === 'deposit' ? entry.deposit_method : entry.withdrawal_source || 'N/A'}
                      {entry.exclude_from_current_cycle && (
                        <span className="cb-pill" style={{ marginLeft: 'var(--cb-space-1)' }}>Mid-cycle</span>
                      )}
                      {entry.is_dual_currency && (
                        <div className="cb-num" style={{ fontSize: '0.6875rem', color: 'var(--cb-ink)', marginTop: '4px', fontWeight: 500 }} data-testid={`dual-currency-info-${entry.id}`}>
                          ↔ {entry.linked_physical_currency === 'INR' ? '₹' : ''}
                          {(entry.linked_physical_amount || 0).toLocaleString('en-IN')} {entry.linked_physical_currency} cash
                        </div>
                      )}
                    </td>
                    <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>{entry.notes || '-'}</td>
                    <td>
                      <div className="flex" style={{ gap: 'var(--cb-space-2)' }}>
                        <button
                          onClick={() => openEditLedger(entry)}
                          data-testid={`edit-ledger-${entry.id}`}
                          className="cb-btn-secondary"
                          style={{ padding: '6px 10px' }}
                          title="Edit entry"
                          disabled={isSubmitting}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openReview(entry, 'ledger')}
                          data-testid={`review-ledger-${entry.id}`}
                          className="cb-btn-primary"
                          disabled={isSubmitting}
                          title="Open full review"
                        >
                          <Eye className="w-4 h-4" />
                          Review
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Expenses */}
      {view.expenses.length > 0 && (
        <div className="mb-8">
          <h2 className="cb-section-title mb-4" data-testid="expenses-section-title">{expenseLabel} ({view.expenses.length})</h2>
          <div className="cb-card" style={{ padding: 0, overflowX: 'auto' }}>
            <table data-testid="pending-expenses-table" className="cb-table" style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  <th>Currency</th>
                  <th className="cb-num">Amount</th>
                  <th className="cb-num">USDT Equiv.</th>
                  <th>Source</th>
                  <th>Notes</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {view.expenses.map((expense) => (
                  <tr key={expense.id}>
                    <td className="cb-num cb-muted" style={{ fontSize: '0.8125rem' }}>{new Date(expense.date).toLocaleDateString()}</td>
                    <td style={{ fontWeight: 500 }}>{expense.category}</td>
                    <td className="cb-muted">{expense.sub_category || '-'}</td>
                    <td>{expense.currency}</td>
                    <td className="cb-num">
                      {(expense.currency === 'INR' ? '₹' : '')}{expense.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                    </td>
                    <td className="cb-num cb-muted">{expense.usdt_equivalent ? expense.usdt_equivalent.toLocaleString() : '-'}</td>
                    <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>{expense.payment_source.replace('_', ' ')}</td>
                    <td className="cb-muted" style={{ fontSize: '0.8125rem' }}>
                      {expense.notes || '-'}
                      {expense.approval_status === 'changes_requested' && (
                        <div>
                          <span
                            className="cb-pill"
                            style={{ marginTop: '4px', fontSize: '0.6875rem', borderColor: 'var(--cb-warning, #b47a00)', color: 'var(--cb-warning, #b47a00)' }}
                            title={(expense.review_notes||[]).slice(-1).map(n => `${n.by}: ${n.note}`).join('') || 'Sent back for changes'}
                            data-testid={`changes-requested-badge-${expense.id}`}
                          >
                            changes requested
                          </span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="flex" style={{ gap: 'var(--cb-space-2)' }}>
                        <button
                          onClick={() => openEditExpense(expense)}
                          data-testid={`edit-expense-${expense.id}`}
                          className="cb-btn-secondary"
                          style={{ padding: '6px 10px' }}
                          title="Edit expense"
                          disabled={isSubmitting}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openReview(expense, 'expense')}
                          data-testid={`review-expense-${expense.id}`}
                          className="cb-btn-primary"
                          disabled={isSubmitting}
                          title="Open full review"
                        >
                          <Eye className="w-4 h-4" />
                          Review
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Pending Items */}
      {view.total_pending === 0 && (
        <div className="cb-card text-center" style={{ padding: 'var(--cb-space-9) var(--cb-space-5)' }} data-testid={`empty-state-${activeTab}`}>
          <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--cb-positive)' }} />
          <h3 className="cb-section-title mb-2">{activeTab === 'changes_requested' ? 'Nothing Awaiting Resubmit' : 'All Caught Up'}</h3>
          <p className="cb-muted">{activeTab === 'changes_requested' ? 'No entries currently sent back for changes' : 'No pending approvals at the moment'}</p>
        </div>
      )}

      {/* Cash Pool Conversions — INR → USDT */}
      {activeTab === 'pending' && view.cash_conversions_inr_to_usdt && view.cash_conversions_inr_to_usdt.length > 0 && (
        <div className="mb-8">
          <h2 className="cb-section-title mb-4" data-testid="cash-inr-to-usdt-section-title">Pending Cash Conversions — INR → USDT ({view.cash_conversions_inr_to_usdt.length})</h2>
          <CashConversionTable
            rows={view.cash_conversions_inr_to_usdt}
            direction="inr_to_usdt"
            endpointBase="/cash-pool/convert"
            currentUser={currentUser}
            currentPoolBalance={currentPoolBalance}
            lastApprovedRate={lastApprovedInrToUsdtRate}
            onActioned={fetchPendingApprovals}
          />
        </div>
      )}

      {/* Cash Pool Conversions — USDT → INR */}
      {activeTab === 'pending' && view.cash_conversions_usdt_to_inr && view.cash_conversions_usdt_to_inr.length > 0 && (
        <div className="mb-8">
          <h2 className="cb-section-title mb-4" data-testid="cash-usdt-to-inr-section-title">Pending Cash Conversions — USDT → INR ({view.cash_conversions_usdt_to_inr.length})</h2>
          <CashConversionTable
            rows={view.cash_conversions_usdt_to_inr}
            direction="usdt_to_inr"
            endpointBase="/cash-pool/usdt-to-inr"
            currentUser={currentUser}
            currentPoolBalance={currentPoolBalance}
            lastApprovedRate={lastApprovedUsdtToInrRate}
            onActioned={fetchPendingApprovals}
          />
        </div>
      )}
          </>
        );
      })()}

      {/* Edit Ledger Modal */}
      {editLedgerModal.open && editLedgerModal.entry && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(11,11,10,0.55)' }} onClick={() => setEditLedgerModal({ open: false, entry: null })}>
          <div className="cb-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Edit Ledger Entry</h2>
              <button 
                onClick={() => setEditLedgerModal({ open: false, entry: null })}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleLedgerEdit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Client</label>
                  <input 
                    type="text" 
                    value={editLedgerModal.entry.client_code} 
                    disabled 
                    className="input-field bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select
                    value={editLedgerModal.entry.type}
                    onChange={(e) => setEditLedgerModal({
                      ...editLedgerModal,
                      entry: { ...editLedgerModal.entry, type: e.target.value }
                    })}
                    className="input-field"
                  >
                    <option value="deposit">Deposit</option>
                    <option value="withdrawal">Withdrawal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input
                    type="date"
                    value={editLedgerModal.entry.date}
                    onChange={(e) => setEditLedgerModal({
                      ...editLedgerModal,
                      entry: { ...editLedgerModal.entry, date: e.target.value }
                    })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Currency</label>
                  <select
                    value={editLedgerModal.entry.currency}
                    onChange={(e) => setEditLedgerModal({
                      ...editLedgerModal,
                      entry: { ...editLedgerModal.entry, currency: e.target.value }
                    })}
                    className="input-field"
                  >
                    <option value="INR">INR</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editLedgerModal.entry.amount}
                    onChange={(e) => setEditLedgerModal({
                      ...editLedgerModal,
                      entry: { ...editLedgerModal.entry, amount: e.target.value }
                    })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Conversion Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editLedgerModal.entry.conversion_rate || ''}
                    onChange={(e) => setEditLedgerModal({
                      ...editLedgerModal,
                      entry: { ...editLedgerModal.entry, conversion_rate: e.target.value }
                    })}
                    className="input-field"
                    placeholder="e.g. 85"
                  />
                </div>
                {editLedgerModal.entry.type === 'deposit' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Deposit Method</label>
                    <select
                      value={editLedgerModal.entry.deposit_method}
                      onChange={(e) => setEditLedgerModal({
                        ...editLedgerModal,
                        entry: { ...editLedgerModal.entry, deposit_method: e.target.value }
                      })}
                      className="input-field"
                    >
                      <option value="cash">Cash</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="usdt_exchange">USDT Exchange</option>
                    </select>
                  </div>
                )}
                {editLedgerModal.entry.type === 'withdrawal' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Withdrawal Source</label>
                    <select
                      value={editLedgerModal.entry.withdrawal_source || ''}
                      onChange={(e) => setEditLedgerModal({
                        ...editLedgerModal,
                        entry: { ...editLedgerModal.entry, withdrawal_source: e.target.value }
                      })}
                      className="input-field"
                    >
                      <option value="">Select Source</option>
                      <option value="cash_pool">Cash Pool</option>
                      <option value="exchange_conversion">Exchange Conversion</option>
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <input
                      type="checkbox"
                      checked={editLedgerModal.entry.exclude_from_current_cycle}
                      onChange={(e) => setEditLedgerModal({
                        ...editLedgerModal,
                        entry: { ...editLedgerModal.entry, exclude_from_current_cycle: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    Exclude from Current Cycle (Mid-cycle entry)
                  </label>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={editLedgerModal.entry.notes || ''}
                    onChange={(e) => setEditLedgerModal({
                      ...editLedgerModal,
                      entry: { ...editLedgerModal.entry, notes: e.target.value }
                    })}
                    className="input-field"
                    rows={2}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditLedgerModal({ open: false, entry: null })}
                  className="cb-btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <LoadingButton type="submit" loading={isSubmitting} className="cb-btn-primary" data-testid="save-ledger-edit">
                  Save Changes
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Expense Modal */}
      {editExpenseModal.open && editExpenseModal.expense && (
        <div className="fixed inset-0 flex items-center justify-center z-50 p-4" style={{ background: 'rgba(11,11,10,0.55)' }} onClick={() => setEditExpenseModal({ open: false, expense: null })}>
          <div className="cb-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={{ padding: 'var(--cb-space-6)' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="cb-section-title">Edit Expense</h2>
              <button 
                onClick={() => setEditExpenseModal({ open: false, expense: null })}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--cb-ink-soft)' }}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleExpenseEdit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input
                    type="date"
                    value={editExpenseModal.expense.date}
                    onChange={(e) => setEditExpenseModal({
                      ...editExpenseModal,
                      expense: { ...editExpenseModal.expense, date: e.target.value }
                    })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <select
                    value={editExpenseModal.expense.category}
                    onChange={(e) => setEditExpenseModal({
                      ...editExpenseModal,
                      expense: { ...editExpenseModal.expense, category: e.target.value, sub_category: '' }
                    })}
                    className="input-field"
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat.category} value={cat.category}>{cat.category}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sub Category</label>
                  <select
                    value={editExpenseModal.expense.sub_category || ''}
                    onChange={(e) => setEditExpenseModal({
                      ...editExpenseModal,
                      expense: { ...editExpenseModal.expense, sub_category: e.target.value }
                    })}
                    className="input-field"
                  >
                    <option value="">Select Sub Category</option>
                    {categories.find(c => c.category === editExpenseModal.expense.category)?.sub_categories.map(sub => (
                      <option key={sub} value={sub}>{sub}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Currency</label>
                  <select
                    value={editExpenseModal.expense.currency}
                    onChange={(e) => setEditExpenseModal({
                      ...editExpenseModal,
                      expense: { ...editExpenseModal.expense, currency: e.target.value }
                    })}
                    className="input-field"
                  >
                    <option value="INR">INR</option>
                    <option value="USDT">USDT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editExpenseModal.expense.amount}
                    onChange={(e) => setEditExpenseModal({
                      ...editExpenseModal,
                      expense: { ...editExpenseModal.expense, amount: e.target.value }
                    })}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Conversion Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editExpenseModal.expense.conversion_rate || ''}
                    onChange={(e) => setEditExpenseModal({
                      ...editExpenseModal,
                      expense: { ...editExpenseModal.expense, conversion_rate: e.target.value }
                    })}
                    className="input-field"
                    placeholder="e.g. 85"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Payment Source</label>
                  <select
                    value={editExpenseModal.expense.payment_source}
                    onChange={(e) => setEditExpenseModal({
                      ...editExpenseModal,
                      expense: { ...editExpenseModal.expense, payment_source: e.target.value }
                    })}
                    className="input-field"
                  >
                    <option value="cash_pool">Cash Pool</option>
                    <option value="exchange">Exchange</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea
                    value={editExpenseModal.expense.notes || ''}
                    onChange={(e) => setEditExpenseModal({
                      ...editExpenseModal,
                      expense: { ...editExpenseModal.expense, notes: e.target.value }
                    })}
                    className="input-field"
                    rows={2}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditExpenseModal({ open: false, expense: null })}
                  className="cb-btn-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <LoadingButton type="submit" loading={isSubmitting} className="cb-btn-primary" data-testid="save-expense-edit">
                  Save Changes
                </LoadingButton>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Large Amount Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: '', id: '', entry: null })}
        onConfirm={() => {
          if (confirmDialog.type === 'approveLedger') {
            doApproveLedger(confirmDialog.id);
          }
        }}
        title={confirmDialog.title || 'Confirm Action'}
        message={confirmDialog.message || 'Are you sure?'}
        confirmText="Yes, Approve"
        cancelText="Cancel"
        variant="warning"
        loading={isSubmitting}
      />

      {/* Full-review dialog (replaces inline approve/reject) */}
      <ReviewEntryDialog
        open={reviewDialog.open}
        entry={reviewDialog.entry}
        entryKind={reviewDialog.kind}
        onClose={closeReview}
        onActioned={fetchPendingApprovals}
        clients={clients}
        users={users}
        exchanges={exchanges}
        currentUserId={JSON.parse(localStorage.getItem('user') || '{}').user_id || null}
      />
    </div>
  );
};

export default AdminApprovals;
